import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wifi, WifiOff, Battery, Signal, Download } from 'lucide-react';

// Mobile App Wrapper - Handles mobile-specific features
const MobileAppWrapper = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isPWA, setIsPWA] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [orientation, setOrientation] = useState(
    window.screen?.orientation?.type || 'portrait-primary'
  );

  // Detect PWA mode
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSPWA = window.navigator.standalone === true;
    setIsPWA(isStandalone || isIOSPWA);
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle PWA install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install prompt after 30 seconds
      setTimeout(() => setShowInstallPrompt(true), 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      setOrientation(window.screen?.orientation?.type || 'portrait-primary');
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.screen?.orientation?.addEventListener('change', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.screen?.orientation?.removeEventListener('change', handleOrientationChange);
    };
  }, []);

  // Handle back button for Android
  useEffect(() => {
    const handlePopState = (e) => {
      // Custom back button handling can be implemented here
      // For now, let browser handle it
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Install PWA
  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  }, [deferredPrompt]);

  // Dismiss install prompt
  const dismissInstallPrompt = () => {
    setShowInstallPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('installPromptDismissed', 'true');
  };

  return (
    <div 
      className={`min-h-dvh bg-[#030305] overflow-x-hidden ${orientation.includes('landscape') ? 'landscape-mode' : ''}`}
      data-pwa={isPWA}
      data-online={isOnline}
    >
      {/* Offline Banner */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-[100] safe-top"
          >
            <div className="flex items-center justify-center gap-2 py-2 px-4 bg-[#FF2A2A] text-white text-sm font-medium">
              <WifiOff className="w-4 h-4" />
              <span>You're offline. Some features may be unavailable.</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PWA Install Prompt */}
      <AnimatePresence>
        {showInstallPrompt && !isPWA && !sessionStorage.getItem('installPromptDismissed') && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-24 left-4 right-4 z-[100] safe-bottom"
          >
            <div className="bg-[#0A0A0F]/95 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-2xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF2A2A] to-[#FFE800] flex items-center justify-center flex-shrink-0">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white mb-1">Install Pulsate</h3>
                  <p className="text-sm text-gray-400 mb-3">
                    Add to your home screen for the best experience
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleInstall}
                      className="px-4 py-2 bg-[#00F0FF] text-black font-semibold rounded-full text-sm"
                    >
                      Install
                    </button>
                    <button
                      onClick={dismissInstallPrompt}
                      className="px-4 py-2 bg-white/10 text-white rounded-full text-sm"
                    >
                      Not now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      {children}
    </div>
  );
};

// Hook for detecting mobile features
export const useMobileFeatures = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [hasNotch, setHasNotch] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const ua = navigator.userAgent;
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua));
    setIsIOS(/iPad|iPhone|iPod/.test(ua));
    setIsAndroid(/Android/.test(ua));
    
    // Detect notch (rough estimation)
    const hasNotchDevice = window.screen.height >= 812 && 
      /iPhone/.test(ua) && 
      CSS.supports('padding-top: env(safe-area-inset-top)');
    setHasNotch(hasNotchDevice);

    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);

  return { isMobile, isIOS, isAndroid, hasNotch, viewportHeight };
};

// Touch gesture hook
export const useTouchGestures = (ref, options = {}) => {
  const [gesture, setGesture] = useState(null);
  const startRef = React.useRef({ x: 0, y: 0 });
  const { 
    onSwipeLeft, 
    onSwipeRight, 
    onSwipeUp, 
    onSwipeDown,
    threshold = 50 
  } = options;

  useEffect(() => {
    const element = ref?.current;
    if (!element) return;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      startRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (e) => {
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - startRef.current.x;
      const deltaY = touch.clientY - startRef.current.y;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (absX > threshold || absY > threshold) {
        if (absX > absY) {
          if (deltaX > 0) {
            setGesture('swipeRight');
            onSwipeRight?.();
          } else {
            setGesture('swipeLeft');
            onSwipeLeft?.();
          }
        } else {
          if (deltaY > 0) {
            setGesture('swipeDown');
            onSwipeDown?.();
          } else {
            setGesture('swipeUp');
            onSwipeUp?.();
          }
        }
      }
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [ref, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, threshold]);

  return gesture;
};

// Vibration feedback hook (for supported devices)
export const useHapticFeedback = () => {
  const vibrate = useCallback((pattern = 10) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);

  const lightTap = useCallback(() => vibrate(10), [vibrate]);
  const mediumTap = useCallback(() => vibrate(25), [vibrate]);
  const heavyTap = useCallback(() => vibrate(50), [vibrate]);
  const success = useCallback(() => vibrate([10, 50, 10]), [vibrate]);
  const error = useCallback(() => vibrate([50, 100, 50]), [vibrate]);

  return { vibrate, lightTap, mediumTap, heavyTap, success, error };
};

export default MobileAppWrapper;
